// @flow strict

export type EntryReferral =
  | 'google'
  | 'bing'
  | 'duckduckgo'
  | 'yahoo'
  | 'ecosia'
  | 'brave'
  | 'yandex'
  | 'baidu'
  | 'other_referral'
  | 'internal'
  | 'direct_or_unknown'
  | 'unavailable'
  | 'not_entry';

export const MAX_REFERRER_LENGTH = 4096;

// Reviewed subset of https://www.google.com/supported_domains, 2026-09-11.
// Only these roots and their exact www hosts are accepted. Coverage is
// intentionally incomplete; never replace this with a google.<anything> rule.
const GOOGLE_ROOT_DOMAINS = new Set([
  'google.com',
  'google.at',
  'google.de',
  'google.it',
  'google.es',
  'google.fr',
  'google.pl',
  'google.pt',
  'google.cz',
  'google.hu',
  'google.nl',
  'google.co.uk',
  'google.ie',
  'google.ch',
  'google.be',
  'google.dk',
  'google.no',
  'google.se',
  'google.fi',
  'google.sk',
  'google.com.au',
  'google.ca',
  'google.co.in',
  'google.co.jp',
  'google.com.br',
  'google.com.mx',
  'google.co.nz',
  'google.co.za',
]);

// Exact search hosts, not provider-wide subdomain suffixes. Mail, documents,
// accounts, maps and other product subdomains must remain other_referral.
const SEARCH_HOSTS: Map<string, EntryReferral> = new Map([
  ['bing.com', 'bing'],
  ['www.bing.com', 'bing'],
  ['duckduckgo.com', 'duckduckgo'],
  ['www.duckduckgo.com', 'duckduckgo'],
  ['html.duckduckgo.com', 'duckduckgo'],
  ['lite.duckduckgo.com', 'duckduckgo'],
  ['search.yahoo.com', 'yahoo'],
  ['ecosia.org', 'ecosia'],
  ['www.ecosia.org', 'ecosia'],
  ['search.brave.com', 'brave'],
  ['yandex.com', 'yandex'],
  ['www.yandex.com', 'yandex'],
  ['yandex.ru', 'yandex'],
  ['www.yandex.ru', 'yandex'],
  ['baidu.com', 'baidu'],
  ['www.baidu.com', 'baidu'],
  ['m.baidu.com', 'baidu'],
]);
const INTERNAL_HOSTS = new Set(['ticketlens.com', 'www.ticketlens.com']);
const normalizedHostname = (value: mixed): string =>
  typeof value === 'string' ? value.toLowerCase().replace(/\.$/, '') : '';

const documentNavigationState = (globalObject: any): 'entry' | 'not_entry' | 'unavailable' => {
  const performanceObject = globalObject && globalObject.performance;
  // Older browsers without either timing interface retain the document-entry
  // approximation. Never fall back after a present interface throws or is invalid.
  if (performanceObject === undefined || performanceObject === null) return 'entry';
  if (typeof performanceObject !== 'object') return 'unavailable';
  const readNavigationEntries = performanceObject.getEntriesByType;
  if (readNavigationEntries !== undefined) {
    if (typeof readNavigationEntries !== 'function') return 'unavailable';
    const entries = readNavigationEntries.call(performanceObject, 'navigation');
    if (!Array.isArray(entries)) return 'unavailable';
    if (entries.length > 0) {
      const entry = entries[0];
      if (!entry || typeof entry !== 'object') return 'unavailable';
      const navigationType = entry.type;
      if (navigationType === 'reload' || navigationType === 'back_forward') return 'not_entry';
      return navigationType === 'navigate' ? 'entry' : 'unavailable';
    }
  }
  const legacyNavigation = performanceObject.navigation;
  if (legacyNavigation === undefined || legacyNavigation === null) {
    // A present modern API without an entry is unavailable timing, not proof
    // of a fresh navigation. Only a browser lacking both APIs uses the fallback.
    return readNavigationEntries === undefined ? 'entry' : 'unavailable';
  }
  if (typeof legacyNavigation !== 'object') return 'unavailable';
  const navigationType = legacyNavigation.type;
  if (navigationType === 1 || navigationType === 2) return 'not_entry';
  return navigationType === 0 ? 'entry' : 'unavailable';
};

export const classifyEntryReferral = (globalObject: any): EntryReferral => {
  try {
    // The caller must pass privacy/enablement gates before invoking this helper.
    // Read only the navigation type, never names, timestamps or other entry data.
    const navigationState = documentNavigationState(globalObject);
    if (navigationState !== 'entry') return navigationState;
    const referrer = globalObject && globalObject.document && globalObject.document.referrer;
    if (typeof referrer !== 'string' || referrer.length > MAX_REFERRER_LENGTH) {
      return 'unavailable';
    }
    if (!referrer) return 'direct_or_unknown';
    if (!/^https?:\/\//i.test(referrer) || /[\u0000-\u0020\u007f]/.test(referrer)) {
      return 'unavailable';
    }
    const parsed = new URL(referrer);
    if (parsed.username || parsed.password) return 'unavailable';
    const hostname = normalizedHostname(parsed.hostname);
    const currentHostname = normalizedHostname(
      globalObject.location && globalObject.location.hostname,
    );
    if (INTERNAL_HOSTS.has(hostname) || (currentHostname && hostname === currentHostname)) {
      return 'internal';
    }
    if (parsed.port) return 'other_referral';
    const googleRoot = hostname.startsWith('www.') ? hostname.slice(4) : hostname;
    if (GOOGLE_ROOT_DOMAINS.has(googleRoot)) {
      // When the browser retains a path, avoid known non-search products.
      // Origin-only referrers cannot distinguish these or paid/organic clicks.
      if (/^\/(?:mail|docs|accounts|maps)(?:\/|$)/i.test(parsed.pathname)) return 'other_referral';
      return 'google';
    }
    return SEARCH_HOSTS.get(hostname) || 'other_referral';
  } catch (error) {
    // No parser fragment, raw URL, hostname or exception is retained or logged.
    return 'unavailable';
  }
};
